import { useCallback, useEffect, useRef, useState } from 'react';

import { RootComponentInstance } from '@uniformdev/canvas';
import { UniformEntryPage } from 'src/pages/uniform-entry/uniform-entry-page';
import { useWAEContext } from 'src/uniform/utils/wae-context-provider';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import UFOSegment from '@atlaskit/react-ufo/segment';
import { ProductKeys } from '@atlassiansox/asc-stargate';

import { CrossFlowTouchpointProvider } from '@/components/cross-flow/touchpoint/context';
import { hasJiraImcCampaignKey } from '@/components/signup/utils/url';
import { useLoggedInHasJira, useLoggedOut } from '@/components/user-context/helpers/cohorts';
import { IntercomProvider } from '@/libraries/intercom/src/provider';
import { LoggedInHeroContainer } from '@/templates/software/jira/logged-in/organic-logged-in/logged-in-hero-container';
import { OrganicLoggedInJiraProductTourOverview } from '@/templates/software/jira/logged-in/organic-logged-in/organic-logged-in-jira-product-tour-overview.template';
import { useRecentJiraSite } from '@/templates/software/jira/logged-in/utils/use-recent-jira-site';
import { LoggedOutOrNoJiraProductTourOverview } from '@/templates/software/jira/logged-out/jira-software-logged-out-page/src/jira-software-logged-out-page.template';
import { experiments } from '@/utils/experiment/manifest';
import { useExperiment, EnrollmentType } from '@/utils/experiment/use-experiment';
import { useExperimentExposure } from '@/utils/experiment/use-experiment-exposure';
import { checkFeatureFlagGate } from '@/utils/feature-flags/check-feature-flag';
import { featureFlags } from '@/utils/feature-flags/feature-flag-manifest';

import { JiraProductTourAiTemplate } from '../../../../templates/paid-landing/src/cfind-6215-frictionless-first-look-jira-v2.template';

import { JiraSoftwarePageLoadingSpinner } from './components/jira-software-page-loading-spinner';

const JIRA_REDIRECT_DESTINATION_PATH = '/jira/for-you';
const JIRA_REDIRECT_SOURCE_PARAM = 'redirectSource';
const JIRA_REDIRECT_ANALYTICS_SUBJECT = 'loggedInJiraProductRedirect';
const JIRA_REDIRECT_EXPERIMENT_NAME = experiments.d1d6redirectLoggedInUsers.experimentName;

export const buildJiraForYouUrl = (siteUrl?: string): string | undefined => {
	if (!siteUrl) {
		return undefined;
	}

	try {
		const jiraForYouUrl = new URL(
			`${JIRA_REDIRECT_DESTINATION_PATH}?tab=recommendedActions`,
			siteUrl,
		);
		jiraForYouUrl.searchParams.set(JIRA_REDIRECT_SOURCE_PARAM, JIRA_REDIRECT_EXPERIMENT_NAME);
		return jiraForYouUrl.href;
	} catch {
		return undefined;
	}
};

export const JiraSoftwarePage = ({ utmCampaign }: { utmCampaign?: string }) => {
	const loggedInHasJira = useLoggedInHasJira();
	const isLoggedOut = useLoggedOut();
	const isWae = checkFeatureFlagGate(featureFlags.dxpMigrationJiraProductTourGate);

	// FYI, this is currently JA only, since we only have JA work space appId
	const isBootIntercom = checkFeatureFlagGate(featureFlags.gwp737EnableIntercomChatJsw);

	const hasImcKey = hasJiraImcCampaignKey(utmCampaign);

	// Jira FFl experiment check
	const jiraFflExperimentCheck = useExperiment(experiments.cfind6469FrictionlessFirstLookJiraV3);
	const isJiraFflVariant =
		jiraFflExperimentCheck.type === EnrollmentType.ENROLLED && jiraFflExperimentCheck.value;

	const redirectLoggedInUsersExperiment = useExperiment(experiments.d1d6redirectLoggedInUsers);
	const shouldRedirectLoggedInUsers =
		redirectLoggedInUsersExperiment.type === EnrollmentType.ENROLLED &&
		redirectLoggedInUsersExperiment.value;

	let template: React.ReactNode = <LoggedOutOrNoJiraProductTourOverview />;

	// For logged in, we will either redirect to Jira, show a "Has Jira" experience,
	// or otherwise the Uniform "logged out" page.
	if (loggedInHasJira) {
		template = shouldRedirectLoggedInUsers ? (
			<RedirectingLoggedInJiraPage />
		) : (
			<LoggedInHeroContainer>
				<OrganicLoggedInJiraProductTourOverview />
			</LoggedInHeroContainer>
		);
	} else {
		if (hasImcKey && isJiraFflVariant && isLoggedOut) {
			template = <JiraProductTourAiTemplate />;
		} else if (isWae) {
			template = <WaeJiraPage />;
		}
	}

	return (
		<UFOSegment name="jira-product-tour-overview">
			<IntercomProvider appId="p0ww63i2" triggerBoot={isBootIntercom}>
				<CrossFlowTouchpointProvider
					productKeys={[ProductKeys.JIRA_SOFTWARE]}
					screenName="wacJswProductTourPage"
				>
					{template}
				</CrossFlowTouchpointProvider>
			</IntercomProvider>
		</UFOSegment>
	);
};

const RedirectingLoggedInJiraPage = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { siteUrl, isResolvingSite } = useRecentJiraSite();
	const [redirectAssignFailed, setRedirectAssignFailed] = useState(false);
	const hasTrackedAttemptRef = useRef(false);
	const hasTrackedFailureRef = useRef(false);
	const jiraForYouUrl = buildJiraForYouUrl(siteUrl);

	const fireRedirectAnalyticsEvent = useCallback(
		({
			action,
			eventType,
			failureReason,
			errorMessage,
		}: {
			action: 'initiated' | 'failed';
			eventType: 'track' | 'operational';
			failureReason?: 'missing_redirect_target' | 'redirect_assign_failed';
			errorMessage?: string;
		}) => {
			createAnalyticsEvent({
				eventType,
				action,
				actionSubject: JIRA_REDIRECT_ANALYTICS_SUBJECT,
				actionSubjectId: JIRA_REDIRECT_EXPERIMENT_NAME,
				source: '/software/jira',
				attributes: {
					experimentName: JIRA_REDIRECT_EXPERIMENT_NAME,
					redirectSource: JIRA_REDIRECT_EXPERIMENT_NAME,
					redirectDestinationPath: JIRA_REDIRECT_DESTINATION_PATH,
					failureReason,
					errorMessage,
				},
			}).fire();
		},
		[createAnalyticsEvent],
	);

	useEffect(() => {
		if (!jiraForYouUrl || redirectAssignFailed || hasTrackedAttemptRef.current) {
			return;
		}

		hasTrackedAttemptRef.current = true;
		fireRedirectAnalyticsEvent({
			action: 'initiated',
			eventType: 'track',
		});

		try {
			window.location.assign(jiraForYouUrl);
		} catch (error) {
			setRedirectAssignFailed(true);

			if (!hasTrackedFailureRef.current) {
				hasTrackedFailureRef.current = true;
				fireRedirectAnalyticsEvent({
					action: 'failed',
					eventType: 'operational',
					failureReason: 'redirect_assign_failed',
					errorMessage: error instanceof Error ? error.message : String(error),
				});
			}
		}
	}, [jiraForYouUrl, redirectAssignFailed, fireRedirectAnalyticsEvent]);

	useEffect(() => {
		if (isResolvingSite || jiraForYouUrl || hasTrackedFailureRef.current) {
			return;
		}

		hasTrackedFailureRef.current = true;
		fireRedirectAnalyticsEvent({
			action: 'failed',
			eventType: 'operational',
			failureReason: 'missing_redirect_target',
		});
	}, [isResolvingSite, jiraForYouUrl, fireRedirectAnalyticsEvent]);

	if (isResolvingSite) {
		return <JiraSoftwarePageLoadingSpinner />;
	}

	return jiraForYouUrl && !redirectAssignFailed ? null : (
		<LoggedInHeroContainer>
			<OrganicLoggedInJiraProductTourOverview />
		</LoggedInHeroContainer>
	);
};

const WaeJiraPage = () => {
	const { composition } = useWAEContext();

	const [jiraFflExposureFired, fireJiraFflExposure] = useExperimentExposure(
		experiments.cfind6469FrictionlessFirstLookJiraV3,
	);

	useEffect(() => {
		if (!jiraFflExposureFired) {
			fireJiraFflExposure();
		}
	}, [jiraFflExposureFired, fireJiraFflExposure]);

	return <UniformEntryPage composition={composition as RootComponentInstance} />;
};
